import { DATE_FORMAT_YMD_HYPHEN } from '@/constants/date-format';
import { cdnAxios } from '@/services/axiosInstances';
import type { PoiContent, ScreenSaverInfo } from '@/services/dto';
import dayjs from 'dayjs';

/**
 * S3 Cache Service for direct S3 access
 * Fetches cached data directly from S3 using the cache structure created by cron jobs
 * Cache structure: {ENVIRONMENT}/cache/{type}/{date}/{filename}
 */
export class S3CacheService {
    /**
     * Get S3 cache path for axios request
     * @param cacheType - Cache type (store-data, poi-data, faq-data, screen-data)
     * @param date - Date in YYYY-MM-DD format
     * @param filename - File name (store_X.json or building_X.json)
     */
    private static getS3CachePath(cacheType: string, date: string, filename: string): string {
        const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
        return `/${environment}/cache/${cacheType}/${date}/${filename}`;
    }

    private static getLocalDateString(): string {
        return dayjs().format(DATE_FORMAT_YMD_HYPHEN);
    }
    
    /**
     * Fetch JSON data from S3 using axios
     */
    private static async fetchJsonFromS3<T = any>(path: string): Promise<any> {
        try {
            return await cdnAxios.get<T>(path);
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 403) {
                throw new Error(`S3 access forbidden (403). Check CDN permissions.`);
            }
            if (status === 404) {
                throw new Error(`S3 file not found (404): ${path}`);
            }

            throw error;
        }
    }

    /**
     * Get store config data directly from S3
     */
    static async getStoreConfigData(storeId: number, date?: string): Promise<any> {
        const targetDate = date || this.getLocalDateString();
        const filename = `store_${storeId}.json`;
        const path = this.getS3CachePath('store-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get POI contents data directly from S3
     */
    static async getPoiContentsData(storeId: number, date?: string): Promise<any> {
        const targetDate = date || this.getLocalDateString();    
        const filename = `store_${storeId}.json`;
        const path = this.getS3CachePath('poi-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get FAQ data directly from S3
     */
    static async getFaqData(storeId: number, date?: string): Promise<any> {
        const targetDate = date || this.getLocalDateString();
        const filename = `store_${storeId}.json`;
        const path = this.getS3CachePath('faq-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get screen saver data directly from S3
     */
    static async getScreenSaverData(buildingId: number, date?: string): Promise<any> {
        const targetDate = date || this.getLocalDateString();
        const filename = `building_${buildingId}.json`;
        const path = this.getS3CachePath('screen-data', targetDate, filename);

        return await this.fetchJsonFromS3(path);
    }

    /**
     * Get processed store config from cache
     */
    static async getProcessedStoreConfig(dataConfig: any): Promise<any> {
        try {
            // Get store data
            const cachedStoreConfig = await this.getStoreConfigFromCache(dataConfig.store.id);
            const buildingPoiContents = (await this.getPoiContentsFromCache(dataConfig.store.id)) || { byBuilding: {} };
            if (cachedStoreConfig) {
                // Add Store Config
                dataConfig.store = { ...dataConfig.store, ...cachedStoreConfig };

                // Add POI contents for each building from cache
                if (dataConfig.store.buildings) {
                    dataConfig.store.buildings = await Promise.all(
                        dataConfig.store.buildings.map(async (building: any) => {
                            return {
                                ...building,
                                poiContents: buildingPoiContents?.byBuilding[building.studioBuildingId],
                                contentsPropertyManagement: dataConfig.store.contentsPropertyManagement,
                            };
                        })
                    );
                }

                // Add FAQs from cache
                // dataConfig.store.faqs = await this.getFaqsFromCache(dataConfig.store.id);
            }

            // Get Screen Saver
            dataConfig.screenSaver = await this.getScreenSaverFromCache({
                buildingId: Number(dataConfig.building.id),
                buildingType: dataConfig.building.buildingType,
                floorId: dataConfig.floor?.id != null ? Number(dataConfig.floor.id) : null,
                orientation: dataConfig.machine.orientation,
            });

            return dataConfig;
        } catch (error) {
            console.error('Error processing store config from cache:', error);
            throw error;
        }
    }

    /**
     * Get screen saver from cache with filtering (like backend)
     */
    private static async getScreenSaverFromCache({
        buildingId,
        buildingType,
        floorId,
        orientation,
    }: {
        buildingId: number;
        buildingType: string | null | undefined;
        floorId: number | string | null;
        orientation: string;
    }): Promise<ScreenSaverInfo[]> {
        try {
            const data = await this.getScreenSaverData(buildingId);
            const filteredData = this.filterScreenDataByOrientationAndFloor(data, orientation, buildingType, floorId);
            return filteredData;
        } catch (error) {
            console.error('Error getting screen saver from cache:', error);
            return [];
        }
    }

    /**
     * Get store config from cache
     */
    private static async getStoreConfigFromCache(storeId: string): Promise<any> {
        try {
            const data = await this.getStoreConfigData(parseInt(storeId, 10));
            return data;
        } catch (error) {
            console.error('Error getting store config from cache:', error);
            return null;
        }
    }

    /**
     * Filter screen saver data by floor ID
     */
    private static filterByFloorId(screenSavers: any[], floorId: number | string | null): any[] {
        if (!screenSavers || !Array.isArray(screenSavers)) {
            return screenSavers;
        }

        if (floorId == null) {
            return screenSavers.filter((screenSaver) => {
                const screenFloorId = screenSaver.floorId;
                return screenFloorId === null || screenFloorId === undefined;
            });
        }

        return screenSavers.filter((screenSaver) => {
            const screenFloorId = screenSaver.floorId;
            return screenFloorId === null || screenFloorId === undefined || String(screenFloorId) === String(floorId);
        });
    }

    private static filterByBuildingType(screenSavers: any[], buildingType: string | null | undefined): any[] {
        if (!screenSavers || !Array.isArray(screenSavers)) {
            return screenSavers;
        }

        if (buildingType == null || buildingType === '') {
            return screenSavers.filter((item) => item.buildingType === null || item.buildingType === undefined);
        }

        return screenSavers.filter((item) => {
            const itemBt = item.buildingType;
            return itemBt === null || itemBt === undefined || String(itemBt) === String(buildingType);
        });
    }

    /**
     * Filter screen saver data by orientation, buildingType and floorId
     */
    private static filterScreenDataByOrientationAndFloor(
        data: any,
        orientation: string,
        buildingType: string | null | undefined,
        floorId: number | string | null
    ): ScreenSaverInfo[] {
        const orientationBucket = data?.[orientation];

        let byBuildingType: any[] = [];
        if (Array.isArray(orientationBucket)) {
            byBuildingType = this.filterByBuildingType(orientationBucket, buildingType);
        } else {
            const orientationData = orientationBucket || {};
            byBuildingType = orientationData[buildingType as string] || [];
        }

        return this.filterByFloorId(byBuildingType, floorId);
    }

    /**
     * Get POI contents from cache
     */
    private static async getPoiContentsFromCache(storeId: string): Promise<{ byBuilding: Record<string, PoiContent[]> } | null> {
        try {
            return await this.getPoiContentsData(parseInt(storeId, 10));
        } catch (error) {
            console.error('Error getting POI contents from cache:', error);
            return null;
        }
    }

    /**
     * Get FAQs from cache
     */
    private static async getFaqsFromCache(storeId: string): Promise<any[]> {
        try {
            const storeData = await this.getFaqData(parseInt(storeId, 10));
            return storeData || [];
        } catch (error) {
            console.error('Error getting FAQs from cache:', error);
            return [];
        }
    }
}
